import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { detectMobile } from './utils/detectMobile';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { ERROR_CODE, routes } from './constants';
import useTokenAuth from './hooks/useTokenAuth';
import { useUserStore } from './store';
import Layout from './layout';
import { SessionExpired } from 'components';
import { ProtectedRoute, routers } from 'routers';
import { useEffect } from 'react';
import { setSentryUser } from 'configs/sentry';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failedCount, err) => {
        const continueRetry = failedCount < 2;
        if (err instanceof AxiosError) {
          return err?.status !== ERROR_CODE.FORBIDDEN && continueRetry;
        }
        return continueRetry;
      },
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
  },
});

function App() {
  const isMobile = detectMobile();
  const isAuthenticated = useUserStore(state => state.isAuthenticated);
  const clientId = useUserStore(state => state.clientId);
  const user = useUserStore(state => state.user);

  const location = useLocation();
  const isSurveyRoute = location.pathname.startsWith(routes.SURVEY_PATH_PREFIX);
  const { isLoading: isTokenAuthLoading, error: tokenAuthError } = useTokenAuth({
    enabled: !isSurveyRoute,
  });

  const isAuthLoading = isSurveyRoute ? false : isTokenAuthLoading;
  const allowUnauthenticatedRoute =
    location.pathname.startsWith(routes.RICHMENU_ADD_FRIEND) ||
    isSurveyRoute;
  const shouldShowSessionExpired =
    !allowUnauthenticatedRoute && !isAuthLoading && (!isAuthenticated || tokenAuthError);

  useEffect(() => {
    if (user) {
      setSentryUser({
        userId: String(user.id),
        nickname: user.nickname,
        clientId: clientId || undefined,
      });
    }
  }, [user, clientId]);

  return (
    <>
      <div
        id="app"
        className={`${!isMobile ? `w-[50vh] relative mx-auto translate-x-0` : ``
          } h-[100dvh] overflow-hidden bg-white`}
      >
        {isAuthLoading ? (
          <div className="h-full w-full bg-white" />
        ) : shouldShowSessionExpired ? (
          <SessionExpired />
        ) : (
          <Routes>
            <Route element={<Layout loading={isAuthLoading} />}>
              <Route path="/" element={<Navigate to={routes.TALK} />} />
              {routers.map(
                ({
                  path,
                  component: PageComponent,
                  isProtected,
                  children,
                }) => (
                  <Route
                    key={path}
                    path={path}
                    element={
                      isProtected ? (
                        <ProtectedRoute>
                          <PageComponent />
                        </ProtectedRoute>
                      ) : (
                        <PageComponent />
                      )
                    }
                  >
                    {children?.map((child: {
                      path: string;
                      component: () => JSX.Element;
                      index?: boolean;
                    }) => {
                      const ChildComponent = child.component;
                      return (
                        <Route
                          key={child.path}
                          path={child.path}
                          element={<ChildComponent />}
                          index={child.index}
                        />
                      );
                    })}
                  </Route>
                )
              )}
              <Route path="*" element={isAuthenticated ? <Navigate to={routes.TALK} /> : <SessionExpired />} />
            </Route>
          </Routes>
        )}
      </div>
      <ToastContainer
        position="bottom-center"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover={false}
        theme="light"
      />
    </>
  );
}

function withQueryClient(App: () => JSX.Element) {
  return () => (
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  );
}

export default withQueryClient(App);
